<template>
  <div class="min-h-screen bg-[#f6f7f9]">

    <!-- ── Hero ── -->
    <section class="bg-white border-b border-[#f0f0f0] py-12 lg:py-16">
      <div class="max-w-screen-xl mx-auto px-6">

        <!-- Breadcrumb -->
        <nav class="flex items-center gap-1.5 text-[0.8rem] text-[#999] mb-6 flex-wrap">
          <router-link to="/" class="hover:text-[#111] transition-colors no-underline"><i class="bi bi-house"></i></router-link>
          <i class="bi bi-chevron-right text-[0.6rem]"></i>
          <span class="text-[#333] font-medium">Retourneren</span>
        </nav>

        <div class="flex flex-col lg:flex-row gap-10 items-center">
          <div class="flex-1">
            <span class="inline-flex items-center gap-1.5 text-[0.75rem] font-bold uppercase tracking-wider text-[#7a5f00] bg-[#fffbe6] border border-[#FFD500] px-3 py-1 rounded-full mb-4">
              <i class="bi bi-arrow-clockwise"></i> Retourbeleid
            </span>
            <h1 class="text-[clamp(1.75rem,4vw,2.75rem)] font-black text-[#111] leading-tight mb-4">
              30 dagen bedenktijd.<br>Geen gedoe.
            </h1>
            <p class="text-[1.0625rem] text-[#555] leading-relaxed mb-6 max-w-[560px]">
              Niet tevreden? Geen probleem. Je hebt <strong>30 dagen</strong> de tijd om een product te retourneren, gratis en zonder vragen. Wij regelen de rest.
            </p>

            <!-- Snelle garanties -->
            <div class="flex flex-wrap gap-3 mb-6">
              <span class="inline-flex items-center gap-2 bg-[#f0fdf4] border border-green-200 text-green-700 text-[0.82rem] font-semibold px-3.5 py-2 rounded-xl">
                <i class="bi bi-check-circle-fill"></i> Gratis retour
              </span>
              <span class="inline-flex items-center gap-2 bg-[#f0fdf4] border border-green-200 text-green-700 text-[0.82rem] font-semibold px-3.5 py-2 rounded-xl">
                <i class="bi bi-check-circle-fill"></i> Geld terug binnen 5 werkdagen
              </span>
              <span class="inline-flex items-center gap-2 bg-[#f0fdf4] border border-green-200 text-green-700 text-[0.82rem] font-semibold px-3.5 py-2 rounded-xl">
                <i class="bi bi-check-circle-fill"></i> 30 dagen bedenktijd
              </span>
            </div>

            <a
              href="mailto:retour@plug2charge.nl"
              class="inline-flex items-center gap-2 bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold px-5 py-3 rounded-xl no-underline transition-all text-[0.9375rem]"
            >
              <i class="bi bi-envelope"></i> Retour aanmelden
            </a>
          </div>

          <!-- Stats -->
          <div class="grid grid-cols-2 gap-3 shrink-0">
            <div class="bg-[#FFD500] rounded-2xl p-5 text-center">
              <div class="text-[2rem] font-black text-[#111] leading-none mb-1">30</div>
              <div class="text-[0.78rem] text-[#111]/60 font-medium">dagen bedenktijd</div>
            </div>
            <div class="bg-[#f8f9fa] rounded-2xl p-5 text-center">
              <div class="text-[2rem] font-black text-[#111] leading-none mb-1">€0</div>
              <div class="text-[0.78rem] text-[#888] font-medium">retourkosten</div>
            </div>
            <div class="bg-[#f8f9fa] rounded-2xl p-5 text-center">
              <div class="text-[2rem] font-black text-[#111] leading-none mb-1">5</div>
              <div class="text-[0.78rem] text-[#888] font-medium">werkdagen terugbetaling</div>
            </div>
            <div class="bg-[#111] rounded-2xl p-5 text-center">
              <div class="text-[2rem] font-black text-white leading-none mb-1">✓</div>
              <div class="text-[0.78rem] text-white/60 font-medium">geen vragen gesteld</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Stap voor stap ── -->
    <section class="py-14 lg:py-20">
      <div class="max-w-screen-xl mx-auto px-6">
        <div class="text-center mb-10">
          <h2 class="text-[clamp(1.4rem,3vw,2rem)] font-black text-[#111] mb-3">Zo werkt retourneren</h2>
          <p class="text-[#888] text-[0.9375rem] max-w-xl mx-auto">In 3 stappen geregeld, snel en zonder gedoe</p>
        </div>

        <div class="grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
          <div class="bg-white rounded-2xl border border-[#e9eaec] p-6 text-center relative">
            <div class="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-[#FFD500] text-[#111] font-black text-[0.875rem] flex items-center justify-center">1</div>
            <i class="bi bi-envelope-fill text-[2rem] text-[#FFD500] mb-3 block mt-2"></i>
            <h3 class="font-bold text-[#111] mb-2 text-[0.9375rem]">Retour melden</h3>
            <p class="text-[0.83rem] text-[#888] leading-relaxed">Stuur een mail naar <a href="mailto:retour@plug2charge.nl" class="text-[#FFD500] hover:underline no-underline font-medium">retour@plug2charge.nl</a> met je ordernummer en reden.</p>
          </div>
          <div class="bg-white rounded-2xl border border-[#e9eaec] p-6 text-center relative">
            <div class="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-[#FFD500] text-[#111] font-black text-[0.875rem] flex items-center justify-center">2</div>
            <i class="bi bi-box-seam-fill text-[2rem] text-[#FFD500] mb-3 block mt-2"></i>
            <h3 class="font-bold text-[#111] mb-2 text-[0.9375rem]">Product inpakken</h3>
            <p class="text-[0.83rem] text-[#888] leading-relaxed">Stuur het product terug in de originele verpakking. Wij sturen je een gratis retourlabel per mail.</p>
          </div>
          <div class="bg-white rounded-2xl border border-[#e9eaec] p-6 text-center relative">
            <div class="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-[#FFD500] text-[#111] font-black text-[0.875rem] flex items-center justify-center">3</div>
            <i class="bi bi-cash-coin text-[2rem] text-[#FFD500] mb-3 block mt-2"></i>
            <h3 class="font-bold text-[#111] mb-2 text-[0.9375rem]">Geld terug</h3>
            <p class="text-[0.83rem] text-[#888] leading-relaxed">Zodra we je retour ontvangen, storten we het bedrag binnen <strong>5 werkdagen</strong> terug op je rekening.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Voorwaarden ── -->
    <section class="bg-white border-y border-[#f0f0f0] py-14 lg:py-20">
      <div class="max-w-screen-xl mx-auto px-6">
        <div class="grid lg:grid-cols-2 gap-12 items-start">

          <!-- Wat kan je retourneren -->
          <div>
            <h2 class="text-[1.25rem] font-black text-[#111] mb-6 flex items-center gap-2">
              <i class="bi bi-check-circle text-green-500"></i> Wat kan je retourneren?
            </h2>
            <ul class="flex flex-col gap-3">
              <li v-for="item in canReturn" :key="item" class="flex items-start gap-3 text-[0.9rem] text-[#444]">
                <i class="bi bi-check2 text-green-500 text-[1rem] shrink-0 mt-0.5"></i>
                {{ item }}
              </li>
            </ul>
          </div>

          <!-- Wat kan NIET terug -->
          <div>
            <h2 class="text-[1.25rem] font-black text-[#111] mb-6 flex items-center gap-2">
              <i class="bi bi-x-circle text-red-400"></i> Wat kan <em>niet</em> terug?
            </h2>
            <ul class="flex flex-col gap-3">
              <li v-for="item in cannotReturn" :key="item" class="flex items-start gap-3 text-[0.9rem] text-[#444]">
                <i class="bi bi-x text-red-400 text-[1.1rem] shrink-0 mt-0.5"></i>
                {{ item }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Wettelijke basis ── -->
    <section class="py-14 lg:py-20">
      <div class="max-w-screen-xl mx-auto px-6">
        <div class="max-w-3xl mx-auto">
          <h2 class="text-[1.25rem] font-black text-[#111] mb-6">Wettelijk retourrecht (Wet Koop op Afstand)</h2>
          <div class="bg-white rounded-2xl border border-[#e9eaec] p-6 lg:p-8 text-[0.9rem] text-[#555] leading-relaxed space-y-4">
            <p>
              Op grond van de Nederlandse <strong>Wet Koop op Afstand</strong> (Burgerlijk Wetboek art. 6:230o e.v.) heb je als consument het recht om een online aankoop binnen <strong>14 kalenderdagen</strong> te herroepen zonder opgave van reden.
            </p>
            <p>
              Bij Plug2Charge hanteren wij een ruimere termijn van <strong>30 dagen</strong>. De bedenktijd begint op de dag nadat je het product hebt ontvangen.
            </p>
            <p>
              Na het melden van je retour heb je nogmaals 14 dagen de tijd om het product daadwerkelijk terug te sturen. Wij betalen het aankoopbedrag inclusief standaard verzendkosten terug binnen 14 dagen na ontvangst van de retour. In de praktijk doen wij dit binnen 5 werkdagen.
            </p>
            <p class="text-[0.82rem] text-[#999] border-t border-[#f0f0f0] pt-4">
              Plug2charge.nl (Slimmemeterwinkel.nl) &nbsp;·&nbsp; KVK: 76253937 &nbsp;·&nbsp; BTW: NL003065325B15 &nbsp;·&nbsp; <a href="mailto:info@plug2charge.nl" class="text-[#FFD500] hover:underline no-underline">info@plug2charge.nl</a>
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Garantie banner ── -->
    <section class="pb-14 lg:pb-20">
      <div class="max-w-screen-xl mx-auto px-6">
        <div class="bg-gradient-to-r from-[#111] to-[#1a1a1a] rounded-2xl px-6 py-8 flex flex-col sm:flex-row items-center gap-6">
          <div class="w-14 h-14 rounded-2xl bg-[#FFD500]/10 border border-[#FFD500]/20 flex items-center justify-center shrink-0">
            <i class="bi bi-shield-check text-[1.75rem] text-[#FFD500]"></i>
          </div>
          <div class="flex-1 text-center sm:text-left">
            <h3 class="text-white font-bold text-[1.05rem] mb-1">Nog vragen over je retour?</h3>
            <p class="text-[#999] text-[0.85rem]">Ons team staat klaar om je te helpen, per mail of live chat.</p>
          </div>
          <div class="flex gap-3 shrink-0">
            <a href="/klantenservice#livechat" class="inline-flex items-center gap-2 bg-white hover:bg-[#f0f0f0] text-[#111] font-bold px-4 py-2.5 rounded-xl no-underline transition-all text-[0.875rem]">
              <i class="bi bi-chat-dots"></i> Live chat
            </a>
            <a href="mailto:retour@plug2charge.nl" class="inline-flex items-center gap-2 bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold px-4 py-2.5 rounded-xl no-underline transition-all text-[0.875rem]">
              <i class="bi bi-envelope"></i> Mailen
            </a>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script setup>
const canReturn = [
  'Producten in originele, onbeschadigde verpakking',
  'Producten die je hebt ontvangen maar nog niet gebruikt',
  'Producten met een fabricagefout of beschadiging bij levering',
  'Verkeerd besteld (verkeerd model, kleur of type)',
  'Alle producten binnen 30 dagen na ontvangst',
]

const cannotReturn = [
  'Producten die al zijn geïnstalleerd of ingebouwd',
  'Producten met zichtbare gebruiksschade die niet bij levering aanwezig was',
  'Producten waarvan de verzegeling is verbroken (bijv. software, batterijcellen)',
  'Maatwerk- of speciaal bestelde producten',
  'Producten die op uitdrukkelijk verzoek zijn besteld buiten het standaard assortiment',
]
</script>
